/**
 * The live-stream refresh bridge (M2-S5): a topic's frames invalidate query
 * keys through coalescers, one per refresh window.
 *
 * `useLiveCacheSync` mounts one bridge for the positions topic. Each window's
 * keys are invalidated at once on the first frame, then at most once per
 * window, with one trailing refresh for the last frame of a burst
 * (`createCoalescer`). Every invalidation passes `cancelRefetch: false`, so a
 * fetch already in flight is kept rather than aborted and restarted: a
 * response slower than the window still lands [RV-9]. The returned disposer
 * unsubscribes and cancels every pending refresh, so an unmounted bridge never
 * fires.
 *
 * Pure: runtime imports are relative with `.ts`; the client, the topic source
 * and the clock are injected, so `node --test` drives it with fakes.
 */
import { createCoalescer, type Clock } from './liveCoalescer.ts';

/** The part of a React Query client the bridge uses. */
export interface InvalidatingClient {
  invalidateQueries(filters: { queryKey: readonly unknown[] }, options: { cancelRefetch: boolean }): unknown;
}

/** The part of the socket the bridge uses (`ws.subscribe`). */
export interface TopicSource {
  subscribe(topic: string, listener: (data: unknown, topic: string) => void): () => void;
}

export interface RefreshWindow {
  windowMs: number;
  keys: ReadonlyArray<readonly unknown[]>;
}

export interface LiveRefreshPlan {
  topic: string;
  windows: readonly RefreshWindow[];
}

export function startLiveRefresh(
  client: InvalidatingClient,
  source: TopicSource,
  plan: LiveRefreshPlan,
  clock?: Clock,
): () => void {
  const coalescers = plan.windows.map(({ windowMs, keys }) => createCoalescer(windowMs, () => {
    for (const queryKey of keys) {
      // One key that throws must not skip the others in its window.
      try {
        void client.invalidateQueries({ queryKey }, { cancelRefetch: false });
      } catch (error) {
        console.error('live refresh failed for', queryKey, error);
      }
    }
  }, clock));
  const unsubscribe = source.subscribe(plan.topic, () => {
    for (const coalescer of coalescers) coalescer.trigger();
  });
  return () => {
    unsubscribe();
    for (const coalescer of coalescers) coalescer.cancel();
  };
}
